/**
 * Manufacturers Page
 * ==================
 * Manage product manufacturers/brands.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Edit2, Trash2, Factory, AlertCircle, X, Package } from 'lucide-react'
import { Card, CardContent, CardHeader } from '@/components/Card'
import Button from '@/components/Button'
import Input from '@/components/Input'
import { useManufacturers, useCreateManufacturer, useDeleteManufacturer, useUpdateManufacturer } from '@/hooks/useManufacturers'
import type { Manufacturer, ManufacturerCreate } from '@/types'

export default function Manufacturers() {
  const { data, isLoading, error } = useManufacturers(true)
  const createManufacturer = useCreateManufacturer()
  const updateManufacturer = useUpdateManufacturer()
  const deleteManufacturer = useDeleteManufacturer()

  const [isAdding, setIsAdding] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [formData, setFormData] = useState<ManufacturerCreate>({ name: '' })
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const manufacturers = data?.data ?? []

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim()) return

    if (editingId) {
      await updateManufacturer.mutateAsync({ id: editingId, data: formData })
      setEditingId(null)
    } else {
      await createManufacturer.mutateAsync(formData)
      setIsAdding(false)
    }
    setFormData({ name: '' })
  }

  const handleEdit = (manufacturer: Manufacturer) => {
    setEditingId(manufacturer.id)
    setFormData({ name: manufacturer.name })
    setIsAdding(false)
  }

  const handleDelete = async (id: number) => {
    if (confirm('Are you sure you want to delete this manufacturer?')) {
      setDeleteError(null)
      try {
        await deleteManufacturer.mutateAsync({ id })
      } catch (error: unknown) {
        const err = error as { response?: { data?: { detail?: string } } }
        const message = err.response?.data?.detail || 'Failed to delete manufacturer'
        setDeleteError(message)
      }
    }
  }

  const handleCancel = () => {
    setIsAdding(false)
    setEditingId(null)
    setFormData({ name: '' })
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400">Error loading manufacturers</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Manufacturers</h1>
          <p className="text-gray-500 dark:text-gray-400">Manage product brands and manufacturers</p>
        </div>
        <Button icon={<Plus size={16} />} onClick={() => setIsAdding(true)}>
          Add Manufacturer
        </Button>
      </div>

      {/* Delete error message */}
      {deleteError && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <p className="text-red-800 dark:text-red-200 font-medium">Cannot delete manufacturer</p>
            <p className="text-red-600 dark:text-red-300 text-sm mt-1">{deleteError}</p>
          </div>
          <button
            onClick={() => setDeleteError(null)}
            className="text-red-500 hover:text-red-700 dark:hover:text-red-300"
          >
            <X size={20} />
          </button>
        </div>
      )}

      {/* Add/Edit form */}
      {(isAdding || editingId) && (
        <Card>
          <CardHeader title={editingId ? 'Edit Manufacturer' : 'New Manufacturer'} />
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Manufacturer Name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="e.g., Dell, HP, Intel"
                required
              />
              <div className="flex gap-2">
                <Button
                  type="submit"
                  loading={createManufacturer.isPending || updateManufacturer.isPending}
                >
                  {editingId ? 'Update' : 'Create'}
                </Button>
                <Button type="button" variant="secondary" onClick={handleCancel}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Manufacturers list */}
      <Card>
        <CardHeader title="All Manufacturers" />
        <div className="divide-y dark:divide-gray-700">
          {isLoading ? (
            <div className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">Loading...</div>
          ) : manufacturers.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <Factory className="mx-auto text-gray-300 dark:text-gray-600 mb-4" size={48} />
              <p className="text-gray-500 dark:text-gray-400">No manufacturers yet</p>
              <button
                className="text-primary-600 dark:text-primary-400 hover:underline mt-2"
                onClick={() => setIsAdding(true)}
              >
                Create your first manufacturer
              </button>
            </div>
          ) : (
            manufacturers.map((manufacturer) => (
              <div
                key={manufacturer.id}
                className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <div className="flex-1 flex items-center gap-4">
                  {manufacturer.logo_url ? (
                    <img
                      src={manufacturer.logo_url}
                      alt={manufacturer.name}
                      className="h-10 w-10 object-contain rounded"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none'
                      }}
                    />
                  ) : (
                    <div className="h-10 w-10 flex items-center justify-center rounded bg-gray-100 dark:bg-gray-700">
                      <Factory className="text-gray-400" size={20} />
                    </div>
                  )}
                  <div>
                    <Link
                      to={`/manufacturers/${manufacturer.id}/products`}
                      className="font-medium text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      {manufacturer.name}
                    </Link>
                  </div>
                </div>
                {'product_count' in manufacturer && (
                  <Link
                    to={`/manufacturers/${manufacturer.id}/products`}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 mr-4 hover:bg-primary-100 hover:text-primary-700 dark:hover:bg-primary-900/50 dark:hover:text-primary-400 transition-colors"
                    title="View products"
                  >
                    <Package size={12} />
                    {(manufacturer as { product_count: number }).product_count}
                  </Link>
                )}
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(manufacturer)}
                  >
                    <Edit2 size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(manufacturer.id)}
                  >
                    <Trash2 size={16} className="text-red-500" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  )
}
